import { type FC, useId, useState } from "react";

import { invalidateEntityHistory } from "@/api/admin/auditLog";
import { type IBrand, useUpdateBrandMutation } from "@/api/admin/brands";
import { QUERY_KEYS, RESPONSE_ANSWERS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import {
  ActionButtons,
  type DataListForDetailsType,
  DefaultImage,
  DetailsBlock,
  DetailsList,
  DetailsTitle,
} from "@/components/atoms";
import { toastError, toastSuccess, toastWarning } from "@/components/molecules";
import { FIELDS_MAPPING } from "@/utils/constants/allFieldsMapping";

import BrandFormDetails from "../../BrandFormDetails/BrandFormDetails";
import { EntityHistory, isMenuEntityCreateLog } from "../../EntityHistory";

interface DetailsProps {
  data: IBrand;
  hasUpdatePermission?: boolean;
}

const Details: FC<DetailsProps> = ({ data, hasUpdatePermission }) => {
  const detailsBrandData: DataListForDetailsType[] = [
    { title: "Brand name (English)", value: data?.nameEn },
    { title: "Brand name (Arabic)", value: data?.nameAr },
    { title: "Legal entity", value: data?.legalEntityName },
    { title: "Registration code", value: data?.commercialRegistration },
    { title: "VAT", value: data?.VAT },
    {
      title: "Description (English)",
      value: data?.descriptionEn,
    },
    {
      title: "Description (Arabic)",
      value: data?.descriptionAr,
    },
    { title: "Country", value: data.country },
    // Show the readable name, falling back to the code so the row is never
    // blank if the API returns a currency we have no name for.
    { title: "Currency", value: data.currencyName ?? data.currency },
    {
      title: "Entity ID",
      value: data.id,
      allowCopy: true,
    },
  ];

  const [isActive, setActive] = useState(false);
  const [isStepValid, setIsStepValid] = useState(false);
  const formID = useId();

  const { mutate: updateBrand, isPending } = useUpdateBrandMutation();

  const handleSubmit = (payload: IBrand) => {
    const { id, properties, ...restData } = { ...data, ...payload };

    updateBrand(
      {
        id: id || "",
        data: {
          ...restData,
          groupId: data.groupId,
        },
      },
      {
        onSuccess: async () => {
          toastSuccess("Brand was updated successfully!");
          setActive(false);
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.brands, id],
          });
          await invalidateEntityHistory({ scope: id });
        },
        onError: error => {
          if (error.message === RESPONSE_ANSWERS.BAD_REQUEST) {
            toastWarning("Add details information to continue.", "Warning!");
          } else {
            toastError(
              "Oops! Something went wrong.",
              () => handleSubmit(payload),
              "Error"
            );
          }
        },
      }
    );
  };

  return (
    <DetailsBlock
      title="Details"
      isEdit={hasUpdatePermission}
      isActive={isActive}
      setActive={setActive}
    >
      <>
        {isActive ? (
          <div>
            <BrandFormDetails
              id={formID}
              defaultValues={data}
              onSubmit={handleSubmit}
              onValidityChange={setIsStepValid}
              classes="px-0 pb-1"
              showGroupSelection={false}
            />
            <ActionButtons
              formID={formID}
              click={() => setActive(false)}
              isStepValid={isStepValid}
              isPending={isPending}
            />
          </div>
        ) : (
          <DetailsList data={detailsBrandData}>
            <div>
              <DetailsTitle title="Logo" />
              <p className="text-sm text-[#71717A] mb-8">
                Must be a .jpg, .gif, .svg or .png with background file smaller
                than 3 MB and at least 500px by 500px
              </p>
              {data?.logoUrl ? (
                <img
                  src={data?.logoUrl}
                  alt="brand logo"
                  className="w-[110px] h-[110px] rounded-xl"
                />
              ) : (
                <DefaultImage classes="w-[110px] h-[110px] rounded-xl" />
              )}
            </div>
          </DetailsList>
        )}
        <EntityHistory
          entityId={data.id || ""}
          entityName={data.nameEn}
          blockName="Details"
          wrapperClass="absolute right-13 top-4.5"
          mapKeyToName={FIELDS_MAPPING.brandDetails}
          excludeLog={isMenuEntityCreateLog}
        />
      </>
    </DetailsBlock>
  );
};

export default Details;
